// src/components/personal/sections/ClientsSection/ClientsSection.tsx
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useClientData } from './hooks/useClientData';
import { useClientStatus } from './hooks/useClientStatus';
import { TEXT_STYLES } from '@/theme';
import { ClientFilters, Client } from './types/types';
import { ResizeHint } from '@/components/ui/Table/ResizeHint';

import Toolbar from './components/Toolbar';
import ClientTable from './components/Table/Table';
import ClientCard from './components/ClientCard/ClientCard';
import StatusSettings from './components/StatusSettings';
import { Container, Flex, Card } from '@/components/ui/Layout';
import Message from '@/components/ui/Message';
import Pagination from '@/components/ui/Table/Pagination';

import { useColumnWidths } from '@/hooks/useColumnWidths';
import { RefreshCw } from 'lucide-react';

interface ClientsSectionProps {
  initialClients?: Client[];
}

const ClientsSection: React.FC<ClientsSectionProps> = ({ 
  initialClients = []
}) => {
  const { t } = useTranslation(['clients', 'common', 'errors']);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [columnWidths, setColumnWidths] = useColumnWidths('clients-table');
  const DEBUG = process.env.NODE_ENV === 'development';
  
  // Используем хук для работы с данными списка клиентов
  const {
    // Данные клиентов
    allClients,
    filteredClients,
    displayedClients,
    
    // Состояние загрузки
    isLoading,
    error: clientsError,
    
    // Методы загрузки данных
    refreshClients,
    exportClientsToCSV,
    
    // Фильтрация
    filters,
    searchQuery,
    showFilters,
    updateFilters,
    setSearchQuery,
    toggleFilters,
    isFiltering,
    resetFilters,
    
    // Пагинация
    pagination,
    setCurrentPage,
    setPageSize,
    
    // Сортировка
    sortColumn,
    sortDirection,
    setSorting,
    
    // Новое поле - общее количество клиентов из API
    totalClientsCount
  } = useClientData({ initialClients });

  // Используем хук для работы со статусами клиентов
  const {
    statusSettings,
    showStatusSettings,
    clientStats,
    autoCalculationRequirements,
    loading: isStatusLoading,
    error: statusError,
    modalError,
    toggleStatusSettings,
    updateStatusSettings,
    handleStatusSettingsSave  
  } = useClientStatus();

  // Обработчики событий UI
  const handleClientSelect = useCallback((clientId: string) => {
    setSelectedClientId(clientId);
  }, []);

  const handleClientCardClose = useCallback(() => {
    setSelectedClientId(null);
  }, []);

  // Обработчик изменения фильтров
  const handleFiltersChange = useCallback((newFilters: ClientFilters) => {
    if (isLoading) {
      console.log('Загрузка в процессе, изменение фильтров отложено');
      return;
    }
    
    // Определяем, активны ли фильтры
    const hasActiveFilters = Object.values(newFilters).some(val => 
      val !== '' && val !== 'all' && val !== undefined && val !== null
    );
    
    // Обновляем фильтры
    updateFilters(newFilters);
    
    // Для отладки
    if (DEBUG) {
      console.log('Применяются фильтры:', {
        фильтры: newFilters,
        активныФильтры: hasActiveFilters
      });
    }
    
    // После обновления фильтров запускаем загрузку данных
    console.log('Запуск обновления данных после изменения фильтров');
    refreshClients(true);
  }, [updateFilters, refreshClients, isLoading, DEBUG]);

  // Обработчик изменения строки поиска
  const handleSearchChange = useCallback((query: string) => {
    console.log('Получен запрос на поиск:', query);
    
    if (isLoading) {
      console.log('Загрузка в процессе, запрос поиска отложен');
      return;
    }
    
    // Проверка на пустую строку, чтобы не блокировать очистку поиска
    const isEmptyQuery = query.trim() === '';
    
    // Устанавливаем поисковый запрос
    setSearchQuery(query);
    
    // После изменения поискового запроса запускаем загрузку данных
    if (!isEmptyQuery || searchQuery) {
      console.log('Запуск обновления данных после изменения поискового запроса');
      refreshClients(true);
    }
  }, [setSearchQuery, refreshClients, isLoading, searchQuery]);

  // Обработчик экспорта
  const handleExport = useCallback(async () => {
    await exportClientsToCSV(filteredClients);
  }, [exportClientsToCSV, filteredClients]);

  // ИСПРАВЛЕНО: Улучшенный обработчик изменения страницы
  const handlePageChange = useCallback((page: number) => {
    console.log(`Изменение страницы на ${page}`);
    
    // Устанавливаем новую страницу без дополнительных проверок на текущую страницу
    setCurrentPage(page);
    
    // Обновляем данные после смены страницы с передачей false для refreshClients,
    // чтобы не выполнять полную перезагрузку всех данных
    refreshClients(false);
    
    // Дополнительная диагностика
    console.log(`Запрошена страница ${page}`);
  }, [setCurrentPage, refreshClients]);
  
  // ИСПРАВЛЕНО: Улучшенный обработчик изменения размера страницы
  const handlePageSizeChange = useCallback((size: number) => {
    console.log(`Изменение размера страницы на ${size}`);
    
    // Устанавливаем новый размер страницы
    setPageSize(size);
    
    // Сбрасываем текущую страницу на первую при изменении размера
    setCurrentPage(1);
    
    // Обновляем данные после изменения размера страницы
    refreshClients(false);
  }, [setPageSize, setCurrentPage, refreshClients]);
  
  // Обработчик сортировки
  const handleSort = useCallback((column: string, direction: 'asc' | 'desc') => {
    setSorting(column, direction);
  }, [setSorting]);

  // Получаем выбранного клиента
  const selectedClient = allClients.find(client => client.id === selectedClientId);

  // Параметры состояния UI
  const showGlobalLoader = isLoading;
  const hasError = Boolean(statusError || clientsError) && !showStatusSettings;
  const canInteract = !isLoading && !hasError;
  const errorMessage = statusError?.message || clientsError || t('errors:data_load_error');

  // Обработчик закрытия модального окна настроек
  const handleStatusSettingsClose = useCallback(() => {
    toggleStatusSettings();
  }, [toggleStatusSettings]);

  // Обработчик изменения ширины столбца
  const handleColumnWidthsChange = useCallback((widths: Record<string, number>) => {
    setColumnWidths(widths);
  }, [setColumnWidths]);

  // Сброс ширины колонок
  const resetColumnWidths = useCallback(() => {
    setColumnWidths({});
  }, [setColumnWidths]);

  // Обработчик обновления данных
  const handleRefresh = useCallback(() => {
    if (isLoading) {
      console.log('Уже идет загрузка, обновление отменено');
      return;
    }
    
    // Всегда обновляем с принудительной загрузкой всех данных при наличии фильтров
    refreshClients(true);
  }, [refreshClients, isLoading]);

  // Обработчик для сброса всех фильтров
  const handleResetFilters = useCallback(() => {
    if (isLoading) {
      console.log('Уже идет загрузка, сброс фильтров отложен');
      return;
    }
    
    // Сбрасываем фильтры
    resetFilters();
    
    // После сброса фильтров обновляем данные
    refreshClients(true);
  }, [resetFilters, refreshClients, isLoading]);

  // Определение общего количества элементов для таблицы
  // Если фильтрация активна, используем количество отфильтрованных элементов
  // Иначе используем общее количество из API
  const totalTableItems = isFiltering ? filteredClients.length : totalClientsCount;
  
  // Вычисляем общее количество страниц
  const calculatedTotalPages = Math.max(1, Math.ceil(totalTableItems / pagination.pageSize));
  
  // ИСПРАВЛЕНО: Добавлены дополнительные отладочные логи
  if (DEBUG) {
    console.log('ClientsSection - Параметры пагинации:', {
      displayedCount: displayedClients.length,
      filteredCount: filteredClients.length,
      totalCount: totalClientsCount,
      totalTableItems,
      isFiltering,
      currentPage: pagination.currentPage,
      pageSize: pagination.pageSize,
      totalPages: calculatedTotalPages,
      isLoading
    });
  }

  return (
    <Container>
      <Flex direction="column" gap="lg">
        {/* Индикатор загрузки */}
        {showGlobalLoader && (
          <Card padding="md" shadow="sm">
            <Flex align="center" justify="center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-2"></div>
              <span className={TEXT_STYLES.body}>
                {isFiltering && isLoading 
                  ? t('loading_all_clients', { defaultValue: 'Загрузка всех клиентов для фильтрации...' }) 
                  : t('common:loading')}
              </span>
            </Flex>
          </Card>
        )}

        {/* Сообщение об ошибке */}
        {hasError && (
          <Message 
            type="error" 
            message={errorMessage}
          />
        )}

        {/* Панель инструментов с фильтрами и поиском */}
        <Toolbar
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          showFilters={showFilters}
          onToggleFilters={toggleFilters}
          onExport={handleExport}
          onOpenSettings={toggleStatusSettings}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onRefresh={handleRefresh}
          onResetFilters={handleResetFilters}
          disabled={!canInteract}
          isFiltering={isFiltering}
          totalItems={totalTableItems}
          isLoading={isLoading}
        />
        
        {/* Таблица клиентов */}
        <Card shadow="sm" padding="none" className="w-full">
          <ResizeHint tableId="clients-table" />
          
          {/* Кнопка сброса колонок, если есть измененные ширины */}
          {Object.keys(columnWidths).length > 0 && (
            <div className="px-4 py-2 flex justify-end">
              <button 
                onClick={resetColumnWidths}
                className="text-xs text-gray-500 hover:text-primary transition-colors flex items-center"
                title={t('common:reset_to_default')}
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                {t('common:reset_columns')}
              </button>
            </div>
          )}
          
          {/* ИСПРАВЛЕНО: Изменены параметры передачи для таблицы */}
          <ClientTable
            filteredClients={displayedClients}
            onClientSelect={handleClientSelect}
            currentPage={pagination.currentPage}
            pageSize={pagination.pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            loading={isLoading}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            columnWidths={columnWidths}
            onColumnWidthsChange={handleColumnWidthsChange}
            scrollable={false}
            autoHeight={true}
            totalItems={totalTableItems}
            totalPages={calculatedTotalPages}
          />
          
          {/* ИСПРАВЛЕНО: Улучшен компонент пагинации - обновлен для работы с блокировкой */}
          {totalTableItems > 0 && (
            <div className="mt-4 px-4 py-2">
              <Pagination 
                currentPage={pagination.currentPage}
                totalPages={calculatedTotalPages}
                totalItems={totalTableItems}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSize={pagination.pageSize}
                itemName={t('table.clients_count_name', { defaultValue: 'клиентов' })}
                disabled={false} // ИСПРАВЛЕНО: Убрана блокировка во время загрузки
              />
            </div>
          )}
        </Card>

        {/* Карточка клиента (если выбран) */}
        {selectedClient && (
          <ClientCard
            client={selectedClient}
            onClose={handleClientCardClose}
            disabled={!canInteract}
          />
        )}

        {/* Модальное окно настроек статусов */}
        {showStatusSettings && statusSettings && (
          <StatusSettings
            isOpen={showStatusSettings}
            onClose={handleStatusSettingsClose}
            settings={statusSettings}
            stats={clientStats}
            error={modalError}
            loading={isStatusLoading}
            onSettingsChange={updateStatusSettings}
            onSave={handleStatusSettingsSave}
            autoCalculationRequirements={autoCalculationRequirements}
          />
        )}
      </Flex>
    </Container>
  );
};

export default ClientsSection;